import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, TextField, MenuItem, Tabs, Tab, Breadcrumbs, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Search, Plus, ChefHat } from 'lucide-react';
import { ClientsFilterBar } from '@/clients/list/components/ClientsFilterBar';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { useDebounce } from '@/hooks/useDebounce';
import { ActionTour, type ActionTourStep } from '@/components/tutorial/ActionTour';
import { getCustomRecipes, deleteCustomRecipe, getCurrentUser, copyCustomRecipe } from '@/lib/apiService';
import { hasIngredientCatalogAccess } from '@/lib/accessControl';
import { isDemoModeEnabled } from '@/demo/config';
import { CustomRecipe } from '@/types/custom-recipe';
import { RecipeCard } from '@/components/recipes/RecipeCard';
import PaginationControls from '@/components/shared/PaginationControls';
import { HEALTH_LABEL_OPTIONS, DIET_LABEL_OPTIONS, MEAL_TYPE_OPTIONS } from '@/constants/dietary-goals';
import { getValidCuisineTypes } from '@/lib/clientPreferenceOptions';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';

const ACCENT = '#2EB4E4';
const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '26px', md: '32px' },
  lineHeight: { xs: '115%', md: '100%' },
  letterSpacing: 0,
  textTransform: 'capitalize',
  color: '#1e293b',
} as const;

const pageSubtitleSx = {
  fontFamily: 'Arial, sans-serif',
  fontWeight: 400,
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: { xs: '20px', sm: '20px' },
  letterSpacing: 0,
  textAlign: 'left',
  color: '#667085',
} as const;

type RecipesTab = 'all' | 'custom';
const FILTER_CONTROL_HEIGHT = 56;
const FILTER_SELECT_SX = {
  width: '100%',
  '& .MuiInputLabel-root': {
    fontSize: '13px',
    fontWeight: 500,
    color: '#94a3b8',
    background: 'white',
    px: 0.5,
    zIndex: 1,
    top: '-2px',
  },
  '& .MuiOutlinedInput-root': {
    height: FILTER_CONTROL_HEIGHT,
    borderRadius: '8px',
    backgroundColor: 'white',
    '& fieldset': {
      top: -5,
      border: '1px solid #e2e8f0',
      '& legend': { display: 'none' },
    },
  },
  '& .MuiSelect-select': {
    height: FILTER_CONTROL_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: 400,
    py: 0,
  },
};

const recipesFilterSelectSx: SxProps<Theme> = {
  width: '100%',
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: '12px',
    letterSpacing: '0.15px',
    verticalAlign: 'middle',
    color: '#94a3b8',
    background: 'white',
    px: 0.5,
    zIndex: 1,
    top: '-2px',
    leadingTrim: 'none',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'white',
    '& fieldset': {
      top: -5,
      border: '1px solid #0000003B',
      '& legend': { display: 'none' },
    },
  },
  '& .MuiSelect-select': { fontSize: '12px', paddingY: '10px' },
};

const recipesActionTourSteps: ActionTourStep[] = [
  {
    id: 'recipes-create',
    title: 'Create Recipe',
    description: 'Open the create-recipe page from here. You will add the recipe details, portions, tags, ingredients, and instructions before saving it to the library.',
  },
  {
    id: 'recipes-ingredients',
    title: 'Ingredients Library',
    description: 'Use this when you need to manage ingredients before building recipes or checking nutrition values.',
  },
  {
    id: 'recipes-tabs',
    title: 'Recipe Tabs',
    description: 'All shows shared and owned recipes. My Recipes narrows the list to recipes created by your account.',
  },
  {
    id: 'recipes-filters',
    title: 'Search and Filters',
    description: 'Search by name or narrow the library by health labels, diet labels, cuisine, and meal type before choosing a recipe.',
  },
  {
    id: 'recipes-list',
    title: 'Recipe Cards',
    description: 'Open a card or use View Recipe to inspect nutrition and ingredients. Owned recipes can be edited or deleted; recipes from others can be copied when available.',
  },
];

const RecipesPage = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.id ?? null;
  const canAccessIngredients = isDemoModeEnabled() || hasIngredientCatalogAccess(currentUser);
  const actionTourSteps = canAccessIngredients
    ? recipesActionTourSteps
    : recipesActionTourSteps.filter((step) => step.id !== 'recipes-ingredients');

  const isRecipeOwnedByCurrentUser = useCallback((recipe: CustomRecipe): boolean => {
    if (typeof recipe.isOwn === 'boolean') {
      return recipe.isOwn;
    }

    if (
      recipe.createdBy
      && typeof recipe.createdBy === 'object'
      && 'id' in recipe.createdBy
      && typeof recipe.createdBy.id === 'string'
      && currentUserId
    ) {
      return recipe.createdBy.id === currentUserId;
    }

    return false;
  }, [currentUserId]);

  const getRecipeCreatedByLabel = useCallback((recipe: CustomRecipe): string => {
    if (
      recipe.createdBy
      && typeof recipe.createdBy === 'object'
      && 'id' in recipe.createdBy
      && typeof recipe.createdBy.id === 'string'
      && currentUserId
      && recipe.createdBy.id === currentUserId
    ) {
      return 'You';
    }

    if (
      recipe.createdBy
      && typeof recipe.createdBy === 'object'
      && 'name' in recipe.createdBy
      && typeof recipe.createdBy.name === 'string'
      && recipe.createdBy.name.trim().length > 0
    ) {
      return recipe.createdBy.name.trim();
    }

    if (isRecipeOwnedByCurrentUser(recipe)) {
      return 'You';
    }

    return 'Another nutritionist';
  }, [currentUserId, isRecipeOwnedByCurrentUser]);

  // State
  const [recipes, setRecipes] = useState<CustomRecipe[]>([]);
  const [activeTab, setActiveTab] = useState<RecipesTab>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoadingRef = useRef(false);
  
  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deletingRecipeId, setDeletingRecipeId] = useState<string | null>(null);
  const [copyingRecipeId, setCopyingRecipeId] = useState<string | null>(null);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHealthLabels, setSelectedHealthLabels] = useState<string[]>([]);
  const [selectedDietLabels, setSelectedDietLabels] = useState<string[]>([]);
  const [selectedCuisineTypes, setSelectedCuisineTypes] = useState<string[]>([]);
  const [selectedMealTypes, setSelectedMealTypes] = useState<string[]>([]);
  
  // Pagination (list for active tab)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecipes, setTotalRecipes] = useState(0);
  const [itemsPerPage] = useState(12);
  /** Totals for tab badges — from lightweight count requests (same filters, both scopes). */
  const [allTabCount, setAllTabCount] = useState(0);
  const [customTabCount, setCustomTabCount] = useState(0);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Debounce filters by creating a string representation that changes when filters change
  const filtersKey = JSON.stringify({
    healthLabels: selectedHealthLabels,
    dietLabels: selectedDietLabels,
    cuisineTypes: selectedCuisineTypes,
    mealTypes: selectedMealTypes,
  });
  const debouncedFiltersKey = useDebounce(filtersKey, 300);

  const buildRecipeListParams = useCallback(
    (tab: RecipesTab, page: number, pageSize: number) => {
      const debouncedFilters = JSON.parse(debouncedFiltersKey);
      return {
        page,
        pageSize: Math.min(pageSize, 100),
        q: debouncedSearchQuery || undefined,
        cuisine: debouncedFilters.cuisineTypes?.length ? debouncedFilters.cuisineTypes.join(',') : undefined,
        mealType: debouncedFilters.mealTypes?.length ? debouncedFilters.mealTypes.join(',') : undefined,
        diet: debouncedFilters.dietLabels?.length ? debouncedFilters.dietLabels.join(',') : undefined,
        healthLabels: debouncedFilters.healthLabels?.length ? debouncedFilters.healthLabels.join(',') : undefined,
        ...(tab === 'custom' && currentUserId ? { nutritionistId: currentUserId } : {}),
      };
    },
    [currentUserId, debouncedSearchQuery, debouncedFiltersKey],
  );

  /** Lightweight requests (pageSize 1) so tab badges show correct totals for All vs My Recipes. */
  const fetchTabCounts = useCallback(async () => {
    try {
      if (currentUserId) {
        const [allResponse, customResponse] = await Promise.all([
          getCustomRecipes(buildRecipeListParams('all', 1, 1)),
          getCustomRecipes(buildRecipeListParams('custom', 1, 1)),
        ]);
        if (allResponse?.success) {
          setAllTabCount(allResponse.pagination?.totalCount ?? 0);
        }
        if (customResponse?.success) {
          setCustomTabCount(customResponse.pagination?.totalCount ?? 0);
        }
      } else {
        const allResponse = await getCustomRecipes(buildRecipeListParams('all', 1, 1));
        if (allResponse?.success) {
          setAllTabCount(allResponse.pagination?.totalCount ?? 0);
        }
        setCustomTabCount(0);
      }
    } catch (err) {
      console.error('Error fetching recipe tab counts:', err);
    }
  }, [buildRecipeListParams, currentUserId]);

  // Fetch recipes (active tab list only)
  const fetchRecipes = useCallback(async (page: number = 1) => {
    // Prevent multiple simultaneous calls
    if (isLoadingRef.current) return;
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const params = buildRecipeListParams(activeTab, page, itemsPerPage);

      if (activeTab === 'custom' && !currentUserId) {
        setError('Sign in to view your recipes.');
        setRecipes([]);
        setTotalPages(1);
        setTotalRecipes(0);
        setCurrentPage(1);
        setLoading(false);
        isLoadingRef.current = false;
        showErrorToast(new Error('Missing account id'), 'Unable to load My Recipes');
        return;
      }

      const response = await getCustomRecipes(params);
      console.log('API Response:', response);
      
      if (response && response.success) {
        setRecipes(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalRecipes(response.pagination?.totalCount || 0);
        setCurrentPage(response.pagination?.page || 1);
        console.log('Pagination data:', {
          totalPages: response.pagination?.totalPages,
          totalCount: response.pagination?.totalCount,
          currentPage: response.pagination?.page
        });
      } else {
        setError(response?.message || 'Failed to fetch recipes');
        // Set empty state on failure
        setRecipes([]);
        setTotalPages(1);
        setTotalRecipes(0);
        setCurrentPage(1);
      }
    } catch (err: unknown) {
      console.error('Error fetching recipes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch recipes');
      // Set empty arrays as fallback
      setRecipes([]);
      setTotalPages(1);
      setTotalRecipes(0);
      setCurrentPage(1);
      showErrorToast(err, "Error");
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [activeTab, currentUserId, buildRecipeListParams, itemsPerPage]);

  // Tab badge totals: both scopes whenever debounced search/filters change (not on tab-only switch)
  useEffect(() => {
    void fetchTabCounts();
  }, [debouncedSearchQuery, debouncedFiltersKey, fetchTabCounts]);

  // List for the active tab: debounced filters/search or tab change
  useEffect(() => {
    void fetchRecipes(1);
  }, [debouncedSearchQuery, debouncedFiltersKey, activeTab, fetchRecipes]);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleTabChange = (_event: React.SyntheticEvent, value: RecipesTab) => {
    setActiveTab(value);
    setCurrentPage(1);
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string, checked: boolean) => {
    switch (filterType) {
      case 'healthLabels':
        setSelectedHealthLabels(prev => 
          checked ? [...prev, value] : prev.filter(item => item !== value)
        );
        break;
      case 'dietLabels':
        setSelectedDietLabels(prev => 
          checked ? [...prev, value] : prev.filter(item => item !== value)
        );
        break;
      case 'cuisineTypes':
        setSelectedCuisineTypes(prev => 
          checked ? [...prev, value] : prev.filter(item => item !== value)
        );
        break;
      case 'mealTypes':
        setSelectedMealTypes(prev => 
          checked ? [...prev, value] : prev.filter(item => item !== value)
        );
        break;
    }
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedHealthLabels([]);
    setSelectedDietLabels([]);
    setSelectedCuisineTypes([]);
    setSelectedMealTypes([]);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchRecipes(page);
  };

  // Handle recipe actions
  const handleViewRecipe = (recipe: CustomRecipe) => {
    // Navigate to recipe details page
    navigate(`/recipes/${recipe.id}`);
  };

  const handleAddToMealPlan = () => {
    navigate('/meal-planning');
  };

  const handleEditRecipe = (recipe: CustomRecipe) => {
    if (!isRecipeOwnedByCurrentUser(recipe)) {
      return;
    }
    // Navigate to edit recipe page
    navigate(`/recipes/${recipe.id}/edit`);
  };

  const handleDeleteRecipe = async (recipe: CustomRecipe) => {
    if (!isRecipeOwnedByCurrentUser(recipe)) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${recipe.title}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeleting(true);
      setDeletingRecipeId(recipe.id);
      await deleteCustomRecipe(recipe.id);
      
      // Remove recipe from local state
      setRecipes(prev => prev.filter(r => r.id !== recipe.id));
      setTotalRecipes(prev => prev - 1);
      void fetchTabCounts();

      showSuccessToast("Recipe Deleted", `"${recipe.title}" has been deleted successfully.`);
    } catch (error) {
      console.error('Error deleting recipe:', error);
      showErrorToast(error, "Error");
    } finally {
      setDeleting(false);
      setDeletingRecipeId(null);
    }
  };

  const handleCopyRecipe = async (recipe: CustomRecipe) => {
    if (isRecipeOwnedByCurrentUser(recipe)) {
      return;
    }

    try {
      setCopyingRecipeId(recipe.id);
      const response = await copyCustomRecipe(recipe.id, { isPublic: false });
      const copiedRecipeId = response.data?.id;

      showSuccessToast('Recipe Copied', 'Your copy is ready to edit.');

      if (copiedRecipeId) {
        navigate(`/recipes/${copiedRecipeId}/edit`);
        return;
      }

      await fetchTabCounts();
      await fetchRecipes(1);
    } catch (error) {
      console.error('Error copying recipe:', error);
      showErrorToast(error, 'Copy Failed');
    } finally {
      setCopyingRecipeId(null);
    }
  };

  const handleCreateRecipe = () => {
    navigate('/recipes/create');
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 2xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card 
          key={index} 
          className=" h-full flex flex-col border shadow-sm"
        >
          {/* Image skeleton */}
          <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 relative overflow-hidden">
            <div className="absolute top-3 right-3">
              <div className="h-6 w-16 bg-gray-300 rounded-full"></div>
            </div>
            <div className="absolute bottom-3 left-3">
              <div className="h-4 w-12 bg-gray-300 rounded"></div>
            </div>
          </div>

          <CardHeader className="pb-3 px-4 pt-4">
            {/* Title skeleton */}
            <div className="h-6 bg-gray-200 rounded mb-2 w-3/4"></div>
            {/* Description skeleton */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <Box data-demo-id="recipes-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '22px',
          border: '1px solid #BDE7F5',
          bgcolor: '#FFFFFF',
        }}
      >
    <div className="space-y-6 lg:pt-0 lg:pb-6 bg-white min-h-full">
      {/* Header */}
      <div className="bg-white px-4 pt-6 pb-2 sm:px-5 lg:px-8">
      <Box
        sx={{
          mb: 4,
          height: 40,
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          px: 2,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#fff',
        }}
      >
        <Breadcrumbs separator="/" aria-label="breadcrumb">
          <Typography sx={{ fontSize: '13px', color: '#334155', fontWeight: 600 }}>
            Recipes
          </Typography>
        </Breadcrumbs>
      </Box>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          <Typography sx={pageTitleSx}>Recipes</Typography>
          <Typography sx={{ ...pageSubtitleSx, width: 'fit-content', maxWidth: '100%', overflowWrap: 'anywhere' }}>
            Manage and assign recipes to your clients
          </Typography>
        </Box>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {canAccessIngredients ? (
            <Button
              data-action-tour-id="recipes-ingredients"
              onClick={() => navigate('/ingredients')}
              className="h-8 gap-1.5 rounded-md border border-[#BDE7F5] bg-white px-3 text-xs font-semibold text-[#1597C9] shadow-none hover:bg-[#EAF9FE]"
            >
              Ingredients
            </Button>
          ) : null}
          <Button
            data-demo-id="recipes-create"
            data-action-tour-id="recipes-create"
            onClick={handleCreateRecipe}
            className="h-8 gap-1.5 rounded-md border-0 px-3 text-xs font-semibold text-white shadow-none hover:brightness-95"
            style={{ backgroundColor: ACCENT }}
          >
            <Plus className="h-3.5 w-3.5" />
            Create Recipe
          </Button>
          <NotificationCenter buttonVariant="feature" />
        </div>
      </div>
      <div data-action-tour-id="recipes-tabs" className="mt-6 flex flex-col gap-4 border-b border-slate-100 pb-0 sm:flex-row sm:items-end sm:justify-between">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Recipe tabs"
          sx={{
            minHeight: 'auto',
            '& .MuiTabs-indicator': {
              backgroundColor: ACCENT,
              height: '2px',
            },
            '& .MuiTab-root': {
              minHeight: 'auto',
              minWidth: 'auto',
              px: 0,
              pb: '12px',
              mr: 3,
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              color: '#64748b',
            },
            '& .MuiTab-root.Mui-selected': {
              color: `${ACCENT} !important`,
              fontWeight: 600,
            },
          }}
        >
          <Tab
            disableRipple
            value="all"
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                All
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '18px',
                    height: '18px',
                    px: '6px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: activeTab === 'all' ? ACCENT : '#f1f5f9',
                    color: activeTab === 'all' ? '#fff' : '#64748b',
                  }}
                >
                  {allTabCount}
                </Box>
              </Box>
            )}
          />
          <Tab
            disableRipple
            value="custom"
            label={(
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                My Recipes
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '18px',
                    height: '18px',
                    px: '6px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: activeTab === 'custom' ? ACCENT : '#f1f5f9',
                    color: activeTab === 'custom' ? '#fff' : '#64748b',
                  }}
                >
                  {customTabCount}
                </Box>
              </Box>
            )}
          />
        </Tabs>
        <div className="flex items-center gap-2 pb-3 sm:pb-2.5 px-1 sm:px-0 w-full sm:w-auto">
          <ClientsFilterBar
            searchQuery={searchQuery}
            onSearchChange={(event) => handleSearch(event.target.value)}
            onSearchClear={() => handleSearch('')}
            showFilterButton={false}
            showExportButton={false}
          />
        </div>
      </div>
      </div>

      {/* Search and Filters */}
      <div data-action-tour-id="recipes-filters" className="px-4 sm:px-5 lg:px-8 mb-6">
        <div className="space-y-3">
          {/* Filter Options */}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] items-stretch mt-2">
            {/* Health Labels */}
            <TextField
              select
              size="small"
              id="health-label"
              label="Health Labels"
              value={selectedHealthLabels[0] || ''}
              onChange={(e) => {
                const val = e.target.value as string;
                if (!val) {
                  setSelectedHealthLabels([]);
                  setCurrentPage(1);
                  return;
                }
                handleFilterChange('healthLabels', val, !selectedHealthLabels.includes(val));
              }}
              sx={FILTER_SELECT_SX}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }} />
              {selectedHealthLabels.length === 0 && (
                <MenuItem value="" sx={{ fontSize: '12px', color: '#94a3b8' }}>
                  Select Health Labels
                </MenuItem>
              )}
              {HEALTH_LABEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Diet Labels */}
            <TextField
              select
              size="small"
              id="diet-label"
              label="Select Diet Labels"
              value={selectedDietLabels[0] || ''}
              onChange={(e) => {
                const val = e.target.value as string;
                if (!val) {
                  setSelectedDietLabels([]);
                  setCurrentPage(1);
                  return;
                }
                handleFilterChange('dietLabels', val, !selectedDietLabels.includes(val));
              }}
              sx={FILTER_SELECT_SX}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }} />
              {selectedDietLabels.length === 0 && (
                <MenuItem value="" sx={{ fontSize: '12px', color: '#94a3b8' }}>
                  Select Diet Labels
                </MenuItem>
              )}
              {DIET_LABEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Cuisine Types */}
            <TextField
              select
              size="small"
              id="cuisine-label"
              label="Select Cuisine Type"
              value={selectedCuisineTypes[0] || ''}
              onChange={(e) => {
                const val = e.target.value as string;
                if (!val) {
                  setSelectedCuisineTypes([]);
                  setCurrentPage(1);
                  return;
                }
                handleFilterChange('cuisineTypes', val, !selectedCuisineTypes.includes(val));
              }}
              sx={FILTER_SELECT_SX}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }} />
              {selectedCuisineTypes.length === 0 && (
                <MenuItem value="" sx={{ fontSize: '12px', color: '#94a3b8' }}>
                  Select Cuisine Type
                </MenuItem>
              )}
              {getValidCuisineTypes().map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Meal Types */}
            <TextField
              select
              size="small"
              id="meal-label"
              label="Select Meal Type"
              value={selectedMealTypes[0] || ''}
              onChange={(e) => {
                const val = e.target.value as string;
                if (!val) {
                  setSelectedMealTypes([]);
                  setCurrentPage(1);
                  return;
                }
                handleFilterChange('mealTypes', val, !selectedMealTypes.includes(val));
              }}
              sx={FILTER_SELECT_SX}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled sx={{ display: 'none' }} />
              {selectedMealTypes.length === 0 && (
                <MenuItem value="" sx={{ fontSize: '12px', color: '#94a3b8' }}>
                  Select Meal Type
                </MenuItem>
              )}
              {MEAL_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <div className="flex h-full">
              <Button
                variant="default"
                onClick={() => {
                  void fetchTabCounts();
                  void fetchRecipes(currentPage);
                }}
                disabled={loading}
                className="flex h-14 w-14 min-w-14 items-center justify-center rounded-lg border-0 bg-[#2EB4E4] p-0 text-white hover:bg-[#22a8d8] shadow-none shrink-0"
                title="Search"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Active Filters */}
          {((selectedHealthLabels && selectedHealthLabels.length > 0) || (selectedDietLabels && selectedDietLabels.length > 0) || 
            (selectedCuisineTypes && selectedCuisineTypes.length > 0) || (selectedMealTypes && selectedMealTypes.length > 0)) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-slate-700">Active filters:</span>
              {selectedHealthLabels && selectedHealthLabels.map((label) => (
                <Tag
                  key={label}
                  variant="neutral"
                  size="sm"
                  onRemove={() => handleFilterChange('healthLabels', label, false)}
                  removeAriaLabel={`Remove ${label.replace(/-/g, ' ')}`}
                >
                  {label.replace(/-/g, ' ')}
                </Tag>
              ))}
              {selectedDietLabels && selectedDietLabels.map((label) => (
                <Tag
                  key={label}
                  variant="neutral"
                  size="sm"
                  onRemove={() => handleFilterChange('dietLabels', label, false)}
                  removeAriaLabel={`Remove ${label.replace(/-/g, ' ')}`}
                >
                  {label.replace(/-/g, ' ')}
                </Tag>
              ))}
              {selectedCuisineTypes && selectedCuisineTypes.map((cuisine) => (
                <Tag
                  key={cuisine}
                  variant="neutral"
                  size="sm"
                  onRemove={() => handleFilterChange('cuisineTypes', cuisine, false)}
                  removeAriaLabel={`Remove ${cuisine}`}
                >
                  {cuisine}
                </Tag>
              ))}
              {selectedMealTypes && selectedMealTypes.map((mealType) => (
                <Tag
                  key={mealType}
                  variant="neutral"
                  size="sm"
                  onRemove={() => handleFilterChange('mealTypes', mealType, false)}
                  removeAriaLabel={`Remove ${mealType.replace(/-/g, ' ')}`}
                >
                  {mealType.replace(/-/g, ' ')}
                </Tag>
              ))}
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#1597C9] hover:bg-[#EAF9FE]">
                Clear all
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="text-center py-8">
            <ChefHat className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load recipes</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button
              onClick={() => {
                void fetchTabCounts();
                void fetchRecipes(currentPage);
              }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <div className="px-4 sm:px-5 lg:px-8">
          {renderSkeleton()}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && (!recipes || recipes.length === 0) && (
        <div className="px-4 sm:px-5 lg:px-8">
          <Card>
            <CardContent className="text-center py-12">
              <ChefHat className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No recipes found</h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || (selectedHealthLabels && selectedHealthLabels.length > 0) || (selectedDietLabels && selectedDietLabels.length > 0) || 
                 (selectedCuisineTypes && selectedCuisineTypes.length > 0) || (selectedMealTypes && selectedMealTypes.length > 0)
                  ? 'Try adjusting your search or filters'
                  : 'Create your first custom recipe to get started'
                }
              </p>
              <Button onClick={handleCreateRecipe} className="gap-2">
                <Plus className="h-4 w-4" />
                Create Recipe
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

          {/* Recipes Grid */}
          {!loading && !error && recipes && recipes.length > 0 && (
            <div data-action-tour-id="recipes-list" className="px-4 sm:px-5 lg:px-8">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 2xl:grid-cols-3">
                {recipes && recipes.map((recipe) => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onView={handleViewRecipe}
                    onAddToMealPlan={handleAddToMealPlan}
                    onEdit={handleEditRecipe}
                    onDelete={handleDeleteRecipe}
                    onCopy={handleCopyRecipe}
                    showActions={true}
                    canEditDelete={isRecipeOwnedByCurrentUser(recipe)}
                    showCopyAction={!isRecipeOwnedByCurrentUser(recipe)}
                    createdByLabel={getRecipeCreatedByLabel(recipe)}
                    isDeleting={deletingRecipeId === recipe.id}
                    isAnyDeleting={deleting}
                    isCopying={copyingRecipeId === recipe.id}
                  />
                ))}
              </div>
            </div>
          )}

      {/* Pagination */}
      {!loading && !error && recipes && recipes.length > 0 && (
        <div className="flex flex-col items-center gap-4 pb-8">
          {/* Pagination Info */}
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecipes)} of {totalRecipes} recipes
          </div>
          
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalCount={totalRecipes}
            pageSize={itemsPerPage}
            className="justify-center"
            showSummary={false}
          />
        </div>
      )}

    </div>
      </Paper>
      <ActionTour
        ariaLabel="Recipes action guide"
        label="Recipes guide"
        storageKey="caloriescience:recipes-action-guide:v1"
        steps={actionTourSteps}
      />
    </Box>
  );
};

export default RecipesPage;
